import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, AlertCircle, Pencil, Trash2, QrCode, Search, Repeat, Truck, Image as ImageIcon, ExternalLink } from 'lucide-react';
import api from '../api/api';
import InventoryItemModal from '../components/InventoryItemModal';
import MovementModal from '../components/MovementModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const APP_URL = window.location.origin;

const CATEGORY_LABELS = {
  filter: 'Filtro', oil: 'Aceite/Lubricante', belt: 'Correa/Banda',
  bearing: 'Rodamiento', tire: 'Neumático', electrical: 'Eléctrico',
  hydraulic: 'Hidráulico', other: 'Otros',
};

export default function AlmacenCarpeta() {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [folder, setFolder] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [movementItem, setMovementItem] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/warehouse/folders/${folderId}`),
      api.get(`/inventory?folder_id=${folderId}`),
    ]).then(([f, i]) => {
      setFolder(f.data);
      setItems(i.data);
      setMovementItem(prev => prev ? i.data.find(x => x.id === prev.id) || null : null);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [folderId]);

  const filtered = items.filter(i =>
    !search || i.name.toLowerCase().includes(search.toLowerCase()) || i.code.toLowerCase().includes(search.toLowerCase())
  );
  const lowStock = items.filter(i => (i.quantity || 0) <= (i.min_quantity || 0));

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.code} · ${item.name}" de esta carpeta?`)) return;
    try {
      await api.delete(`/inventory/${item.id}`);
      load();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'No se pudo eliminar el repuesto');
    }
  };

  if (loading && !folder) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
    </div>
  );

  return (
    <div className="p-4 md:p-8 page-enter">
      <div className="flex items-start gap-3 md:gap-4 mb-5">
        <button
          onClick={() => folder ? navigate(`/inventario/area/${folder.area_id}`) : navigate('/inventario')}
          className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 mt-1 flex-shrink-0"
          data-testid="back-to-folders"
        ><ArrowLeft size={16} /></button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-400 mb-0.5 truncate">Almacén · {folder?.area_name}</p>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 truncate" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {folder?.name}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {items.length} referencia(s)
            {lowStock.length > 0 && <span className="text-amber-700 font-medium"> · {lowStock.length} bajo mínimos</span>}
          </p>
          {folder?.equipment_id && (
            <button
              onClick={() => navigate(`/equipos/${folder.equipment_id}`)}
              className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
              data-testid="go-to-equipment"
            >
              <Truck size={13} /> Ver ficha del activo <ExternalLink size={11} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <a
            href={`${API_URL}/api/warehouse/folders/${folderId}/qr?base_url=${encodeURIComponent(APP_URL)}`}
            target="_blank" rel="noreferrer"
            className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500"
            title="QR para la estantería"
            data-testid="folder-detail-qr"
          ><QrCode size={16} /></a>
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-3 md:px-4 py-2 rounded-lg"
            data-testid="add-item-btn"
          >
            <Plus size={16} /> <span className="hidden sm:inline">Nuevo Repuesto</span>
          </button>
        </div>
      </div>

      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3" data-testid="folder-low-stock">
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">Reponer en esta carpeta</p>
            <p className="text-xs text-amber-800 mt-0.5">{lowStock.map(i => `${i.code} (${i.quantity}/${i.min_quantity})`).join(' · ')}</p>
          </div>
        </div>
      )}

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text" placeholder="Buscar por código o artículo..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full max-w-md pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16a34a]"
          data-testid="search-items"
        />
      </div>

      {/* Mobile: card list (shelf / QR use case) */}
      <div className="md:hidden space-y-2" data-testid="items-mobile-list">
        {loading ? (
          <p className="text-center text-gray-400 text-sm py-8">Cargando...</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-gray-400 text-sm py-10" data-testid="no-items-mobile">
            {items.length === 0 ? 'Aún no hay repuestos en esta carpeta.' : 'Sin resultados para la búsqueda.'}
          </p>
        ) : filtered.map(item => {
          const isLow = (item.quantity || 0) <= (item.min_quantity || 0);
          return (
            <div key={item.id} className={`bg-white rounded-xl border shadow-sm p-3 ${isLow ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'}`} data-testid={`item-card-${item.id}`}>
              <div className="flex gap-3">
                {item.photo_url ? (
                  <button onClick={() => setPhotoPreview(item)} className="w-14 h-14 rounded-lg border border-gray-200 overflow-hidden flex-shrink-0" data-testid={`item-card-photo-${item.id}`}>
                    <img src={`${API_URL}${item.photo_url}`} alt={item.name} className="w-full h-full object-cover" />
                  </button>
                ) : (
                  <div className="w-14 h-14 rounded-lg border border-dashed border-gray-200 flex items-center justify-center flex-shrink-0">
                    <ImageIcon size={18} className="text-gray-300" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[11px] text-gray-500">{item.code}</p>
                  <p className="font-semibold text-sm text-gray-900 leading-tight">{item.name}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    {CATEGORY_LABELS[item.category] || item.category}{item.location ? ` · ${item.location}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`font-mono font-bold text-lg leading-none ${isLow ? 'text-amber-600' : 'text-gray-900'}`}>{item.quantity}</p>
                  <p className="text-[10px] text-gray-400">{item.unit}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">mín. {item.min_quantity}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-gray-100">
                <button onClick={() => setMovementItem(item)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium bg-[#16a34a] text-white rounded-lg"
                  data-testid={`movement-card-btn-${item.id}`}>
                  <Repeat size={14} /> Entrada / Salida
                </button>
                <button onClick={() => setEditItem(item)}
                  className="p-2 text-gray-500 border border-gray-200 rounded-lg"
                  data-testid={`edit-item-card-${item.id}`}><Pencil size={15} /></button>
                <button onClick={() => handleDelete(item)}
                  className="p-2 text-red-600 border border-red-200 rounded-lg"
                  data-testid={`delete-item-card-${item.id}`}><Trash2 size={15} /></button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase w-14">Foto</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Código</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Artículo</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Categoría</th>
              <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Stock</th>
              <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Mínimo</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Ubicación</th>
              <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Cargando...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400 text-sm" data-testid="no-items">
                {items.length === 0 ? 'Aún no hay repuestos en esta carpeta.' : 'Sin resultados para la búsqueda.'}
              </td></tr>
            ) : filtered.map(item => {
              const isLow = (item.quantity || 0) <= (item.min_quantity || 0);
              return (
                <tr key={item.id} className={`table-row-hover ${isLow ? 'bg-amber-50/40' : ''}`} data-testid={`item-row-${item.id}`}>
                  <td className="px-3 py-2">
                    {item.photo_url ? (
                      <button onClick={() => setPhotoPreview(item)} className="block w-9 h-9 rounded border border-gray-200 overflow-hidden" data-testid={`item-photo-${item.id}`}>
                        <img src={`${API_URL}${item.photo_url}`} alt={item.name} className="w-full h-full object-cover" />
                      </button>
                    ) : (
                      <div className="w-9 h-9 rounded border border-dashed border-gray-200 flex items-center justify-center">
                        <ImageIcon size={14} className="text-gray-300" />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs text-gray-600">{item.code}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {isLow && <AlertCircle size={13} className="text-amber-500 flex-shrink-0" />}
                      <span className="font-medium text-gray-900 text-xs">{item.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-600">{CATEGORY_LABELS[item.category] || item.category}</td>
                  <td className="px-3 py-3 text-right">
                    <span className={`font-mono font-bold text-sm ${isLow ? 'text-amber-600' : 'text-gray-900'}`}>{item.quantity}</span>
                    <span className="text-xs text-gray-400 ml-1">{item.unit}</span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-xs text-gray-500">{item.min_quantity}</td>
                  <td className="px-3 py-3 text-xs text-gray-500">{item.location || '—'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setMovementItem(item)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Entrada / salida de stock" data-testid={`movement-btn-${item.id}`}><Repeat size={15} /></button>
                      <button onClick={() => setEditItem(item)}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Editar repuesto" data-testid={`edit-item-${item.id}`}><Pencil size={15} /></button>
                      <button onClick={() => handleDelete(item)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Eliminar repuesto" data-testid={`delete-item-${item.id}`}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showNew && folder && (
        <InventoryItemModal areaId={folder.area_id} folderId={folderId} onClose={() => setShowNew(false)} onSaved={load} />
      )}
      {editItem && folder && (
        <InventoryItemModal item={editItem} areaId={folder.area_id} folderId={folderId} onClose={() => setEditItem(null)} onSaved={load} />
      )}
      {movementItem && (
        <MovementModal item={movementItem} onClose={() => setMovementItem(null)} onSaved={load} />
      )}
      {photoPreview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPhotoPreview(null)} data-testid="photo-preview-overlay">
          <div className="bg-white rounded-xl overflow-hidden max-w-md w-full">
            <img src={`${API_URL}${photoPreview.photo_url}`} alt={photoPreview.name} className="w-full object-contain max-h-[70vh]" />
            <p className="px-4 py-3 text-sm font-medium text-gray-700">{photoPreview.code} · {photoPreview.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}
